import {
	BEST_MARK_COMPETITION_MAX,
	type EventGender,
	type EventQualifyingStandard,
	type ResultType,
	SHORTLIST_DEADLINE_STATES,
	SHORTLIST_ROLES,
	type ShortlistDeadline,
	type ShortlistDeadlineState,
	type ShortlistEntryRefusal,
	type ShortlistRole,
	formatMark,
	shortlistDeadlineState,
} from "@seagames/contracts";
import { Modal, useUiStrings } from "@seagames/ui";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { i18n } from "../../lib/i18n";
import { formatDateTime, formatDayShort, formatShort } from "../../lib/format/datetime";
import type { StatusGuideGroup } from "../common/status-guide";
import { CalendarIcon } from "../accommodation/icons";
import { Bar } from "../accommodation/parts";
import { AlertTriangleIcon, CheckIcon, LockIcon } from "../accreditation/icons";
import { CloseIcon } from "../transport/icons";
import { type Cell, type QuotaView, type RecordDraft, eventFill, recordVerdict } from "./model";
import "./shortlist.css";
import { DateInput } from "../common/date-input";

/**
 * Small pieces both Shortlist pages share: the deadline pill and tile, the event
 * gender tag, the role tag, the Primary | Reserve | — control, the quota
 * box, the refusal line, the Statuses guide and the "How it works" sheet.
 * Every sentence is a key in the `shortlist` namespace.
 */

const DEADLINE_TONE: Record<ShortlistDeadlineState, string> = {
	none: "bg-gms-navy-soft text-gms-navy",
	open: "bg-gms-ok-soft text-gms-ok",
	upcoming: "bg-gms-warn-soft text-gms-warn",
	closed: "bg-gms-bad-soft text-gms-bad",
};

/** The deadline's state (contracts' `shortlistDeadlineState`): No deadline, Open, Not open yet, Closed (lock). */
export function DeadlinePill({ state }: { state: ShortlistDeadlineState }) {
	const { t } = useTranslation("shortlist");
	return (
		<span
			className={`inline-flex h-[1.25rem] items-center gap-[0.25rem] rounded-[0.25rem] px-[0.4375rem] text-[0.65625rem] font-semibold whitespace-nowrap ${DEADLINE_TONE[state]}`}
		>
			{state === "closed" && <LockIcon className="size-[0.75rem]" />}
			{t(`deadline.state.${state}`)}
		</span>
	);
}

/**
 * The DEADLINE tile's value and note: the deadline (DD/MM/YYYY, time —
 * viewer-local, absolute; the zone in the tooltip) or "No deadline", and the
 * pill under it; a window still to open says when.
 */
export function DeadlineValue({ deadline }: { deadline: ShortlistDeadline }) {
	const { t } = useTranslation("shortlist");
	if (deadline.closes_at === null) return <>{t("deadline.none")}</>;
	return (
		<span className="text-[1rem] tabular-nums" title={formatDateTime(deadline.closes_at)}>
			{formatShort(deadline.closes_at)}
		</span>
	);
}

/** The tile's note: the pill, and when a window still to open opens. */
export function DeadlineNote({ deadline }: { deadline: ShortlistDeadline }) {
	const { t } = useTranslation("shortlist");
	const state = shortlistDeadlineState(deadline);
	return (
		<span className="mt-[0.25rem] inline-flex flex-wrap items-center gap-[0.375rem]">
			<DeadlinePill state={state} />
			{state === "upcoming" && deadline.next_opens_at && (
				<span className="text-[0.75rem] text-gms-ink-2" title={formatDateTime(deadline.next_opens_at)}>
					{t("deadline.opens", { at: formatShort(deadline.next_opens_at) })}
				</span>
			)}
		</span>
	);
}

/** Her `.gt--M/W/X`: the event's gender in a coloured tag. */
export function GenderTag({ gender }: { gender: EventGender }) {
	const { t } = useTranslation("shortlist");
	return (
		<i className="sl-gt not-italic" data-g={gender}>
			{t(`eventGender.${gender}`)}
		</i>
	);
}

/** Her `.etag--P/R`: an event name with P / R after it (By athlete). */
export function RoleTag({ role, children }: { role: ShortlistRole; children?: React.ReactNode }) {
	const { t } = useTranslation("shortlist");
	return (
		<span className="inline-flex max-w-full items-center gap-[0.25rem] rounded-[0.25rem] bg-gms-soft px-[0.375rem] py-[0.125rem] text-[0.75rem] text-gms-navy-ink">
			{children && <span className="truncate">{children}</span>}
			<span className="sl-role" data-role={role} title={t(`role.${role}`)}>
				{t(`role.short.${role}`)}
			</span>
		</span>
	);
}

export interface RoleControlProps {
	value: Cell;
	onChange: (role: Cell) => void;
	/** The caller may not change rows (`can_edit` false): the choice shows, locked. */
	locked: boolean;
	/** PRIMARY would pass the quota (and the cell is not PRIMARY already). */
	primaryFull: boolean;
	/** A TEAM event whose OVR team entry holds the person: the cell cannot be cleared. In an INDIVIDUAL event the row IS the entry (0073) and removing it is free — the API refuses TEAM only. */
	entered: boolean;
	state?: "pending" | "refused";
	/** aria-label of the group: "Role in 100m Hurdles for Porntip Boonma". */
	label: string;
	/** aria-label of the X: "Remove Porntip Boonma from the 100m Hurdles shortlist". */
	removeLabel: string;
}

/**
 * Her segmented `Primary | Reserve` (`.evr .seg`), plus an X that takes the
 * athlete off the shortlist (user, 2026-11-03: a remove button instead of a
 * "Not entered" option). The roles are a radio group: one tab stop, arrow
 * keys move. Disabled controls carry their reason in the tooltip — the API
 * still judges every row on save.
 */
export function RoleControl({ value, onChange, locked, primaryFull, entered, state, label, removeLabel }: RoleControlProps) {
	const { t } = useTranslation("shortlist");
	const options: { v: Exclude<Cell, null>; label: string; disabled: boolean; title?: string }[] = [
		{
			v: "PRIMARY",
			label: t("role.PRIMARY"),
			disabled: locked || (primaryFull && value !== "PRIMARY"),
			title: !locked && primaryFull && value !== "PRIMARY" ? t("quota.fullHint") : undefined,
		},
		{ v: "RESERVE", label: t("role.RESERVE"), disabled: locked },
	];
	const move = (from: number, dir: 1 | -1) => {
		for (let i = 1; i <= options.length; i++) {
			const o = options[(from + dir * i + options.length) % options.length];
			if (o && !o.disabled) return o.v;
		}
		return undefined;
	};
	// Nothing picked: the first role that can be picked holds the tab stop.
	const tabStop = value ?? options.find((o) => !o.disabled)?.v;
	const removeBlocked = entered && value !== null;
	return (
		<div className="sl-rolepick">
			<div role="radiogroup" aria-label={label} className="sl-seg" data-state={state} data-locked={locked ? "" : undefined}>
				{options.map((o, i) => {
					const on = o.v === value;
					return (
						<button
							key={o.v}
							type="button"
							role="radio"
							aria-checked={on}
							data-v={o.v}
							tabIndex={o.v === tabStop ? 0 : -1}
							disabled={o.disabled}
							title={o.title}
							onClick={() => !on && onChange(o.v)}
							onKeyDown={(e) => {
								if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
								e.preventDefault();
								const next = move(i, e.key === "ArrowRight" ? 1 : -1);
								if (next !== undefined && next !== value) onChange(next);
							}}
						>
							{o.label}
						</button>
					);
				})}
			</div>
			{/* Hidden, not removed, while nothing is picked: the row keeps its width. */}
			{!locked && (
				<button
					type="button"
					className="sl-rolepick__remove"
					data-hidden={value === null ? "" : undefined}
					disabled={value === null || removeBlocked}
					aria-hidden={value === null || undefined}
					tabIndex={value === null ? -1 : undefined}
					aria-label={removeLabel}
					title={removeBlocked ? t("role.enteredHint") : t("role.removeHint")}
					onClick={() => onChange(null)}
				>
					<CloseIcon className="size-[0.875rem]" />
				</button>
			)}
		</div>
	);
}

/** Her `.evc__q`: "Primary 1 / 2 · 1 place left", "Quota full", or "No limit". */
export function QuotaBox({ quota }: { quota: QuotaView }) {
	const { t } = useTranslation("shortlist");
	const { formatNumber: fmt } = useUiStrings();
	const tone = quota.over ? "text-gms-bad" : quota.full ? "text-gms-ok" : quota.limit === null ? "text-gms-ink-3" : "text-gms-navy-ink";
	return (
		<span className={`grid text-right text-[0.75rem] font-semibold whitespace-nowrap tabular-nums ${tone}`}>
			{quota.limit === null
				? t("quota.primaryCount", { count: quota.primary, n: fmt(quota.primary) })
				: t("quota.primaryOf", { used: fmt(quota.primary), limit: fmt(quota.limit) })}
			<small className="text-[0.6875rem] font-normal text-gms-ink-3">
				{quota.limit === null
					? t("quota.noLimit")
					: quota.over
						? t("quota.over", { count: quota.primary - quota.limit, n: fmt(quota.primary - quota.limit) })
						: quota.full
							? t("quota.full")
							: t("quota.left", { count: quota.left ?? 0, n: fmt(quota.left ?? 0) })}
				{quota.reserve > 0 && <> · {t("quota.reserves", { count: quota.reserve, n: fmt(quota.reserve) })}</>}
			</small>
		</span>
	);
}

/** Up to this many Primary places draw as pips; a larger quota draws a bar. */
const QUOTA_PIPS_MAX = 12;

/**
 * The event card's quota: "Primary 2 / 4" with a pip per place (filled =
 * taken, red = over the quota) and what is left under it. Colour follows
 * `eventFill`: grey with none, navy while places are left, green when full.
 */
export function QuotaMeter({ quota }: { quota: QuotaView }) {
	const { t } = useTranslation("shortlist");
	const { formatNumber: fmt } = useUiStrings();
	const { limit, primary } = quota;
	const pips = limit !== null && limit > 0 && Math.max(limit, primary) <= QUOTA_PIPS_MAX ? Math.max(limit, primary) : 0;
	return (
		<span className="sl-qm" data-fill={quota.over ? "over" : eventFill(primary, limit)}>
			<span className="sl-qm__head">
				<span className="sl-qm__label">{t("role.PRIMARY")}</span>
				<b className="sl-qm__n">{limit === null ? fmt(primary) : t("quota.ratio", { used: fmt(primary), limit: fmt(limit) })}</b>
			</span>
			{pips > 0 ? (
				<span className="sl-qm__pips" aria-hidden>
					{Array.from({ length: pips }, (_, i) => (
						<i key={i} data-on={i < primary ? (limit !== null && i >= limit ? "over" : "on") : undefined} />
					))}
				</span>
			) : (
				limit !== null && <Bar value={Math.min(primary, limit)} max={limit} className="w-[6rem]" />
			)}
			<small className="sl-qm__note">
				{limit === null
					? t("quota.noLimit")
					: quota.over
						? t("quota.over", { count: primary - limit, n: fmt(primary - limit) })
						: quota.full
							? t("quota.full")
							: t("quota.left", { count: quota.left ?? 0, n: fmt(quota.left ?? 0) })}
				{quota.reserve > 0 && <> · {t("quota.reserves", { count: quota.reserve, n: fmt(quota.reserve) })}</>}
			</small>
		</span>
	);
}

/**
 * M7b: the number the organisation declared for the event in Entry by number
 * (athletes, or teams for a team event), beside the limit. Display only — it
 * never changes the quota above it. Nothing when none was declared.
 */
export function DeclaredEntries({ n }: { n: number | null }) {
	const { t } = useTranslation("entries");
	const { formatNumber: fmt } = useUiStrings();
	if (n === null) return null;
	return (
		<span className="text-[0.6875rem] whitespace-nowrap text-gms-ink-3 tabular-nums" title={t("shortlist.declaredHint")}>
			{t("shortlist.declared", { n: fmt(n) })}
		</span>
	);
}

/** A result type that can hold a mark (a standard is never set on NONE). */
type MarkType = Exclude<ResultType, "NONE">;

/** A canonical mark as the sport writes it, with its unit: "1:58.50", "7.20 m", "250 kg". */
export function markLabel(resultType: ResultType, value: number): string {
	if (resultType === "NONE") return "";
	return i18n.t(`shortlist:record.value.${resultType}`, { mark: formatMark(resultType, Number(value)) });
}

/** Why the API left one row of a batch alone — the `errors` namespace's own sentences where they exist. */
export function refusalText(r: ShortlistEntryRefusal, standard?: EventQualifyingStandard | null): string {
	switch (r.reason) {
		case "mark_required":
			return standard
				? i18n.t("shortlist:refusal.mark_required_standard", { standard: markLabel(standard.result_type, standard.mark) })
				: i18n.t("shortlist:refusal.mark_required");
		case "below_standard":
			return standard && r.mark !== undefined && r.standard !== undefined
				? i18n.t("shortlist:refusal.below_standard_marks", { mark: markLabel(standard.result_type, r.mark), standard: markLabel(standard.result_type, r.standard) })
				: i18n.t("shortlist:refusal.below_standard");
		case "quota_full":
			return r.limit !== undefined && r.used !== undefined
				? i18n.t("errors:SHORTLIST_QUOTA_FULL_limit", { limit: r.limit, used: r.used })
				: i18n.t("errors:SHORTLIST_QUOTA_FULL");
		case "not_eligible":
			return r.ineligible ? i18n.t(`errors:SHORTLIST_NOT_ELIGIBLE_${r.ineligible}`) : i18n.t("errors:SHORTLIST_NOT_ELIGIBLE");
		case "deadline_passed":
			return i18n.t("shortlist:refusal.deadline_passed");
		case "entered":
			return i18n.t("errors:SHORTLIST_ENTERED");
		default:
			return i18n.t("shortlist:refusal.not_in_scope");
	}
}

/** The red line under a refused row. */
export function RefusalLine({ refusal, standard }: { refusal: ShortlistEntryRefusal | undefined; standard?: EventQualifyingStandard | null }) {
	if (!refusal) return null;
	return (
		<p role="alert" className="text-[0.75rem] leading-snug text-gms-bad">
			{refusalText(refusal, standard)}
		</p>
	);
}

/**
 * 0062: an event's qualifying standard under its name: "Qualifying: 10.75
 * or better", the direction in the tooltip, the host's note below.
 */
export function StandardLine({ standard }: { standard: EventQualifyingStandard | null }) {
	const { t } = useTranslation("shortlist");
	if (!standard) return null;
	return (
		<span className="mt-[0.25rem] grid gap-[0.0625rem] text-[0.75rem] leading-snug">
			<span className="flex items-center gap-[0.25rem] font-medium text-gms-navy-ink" title={t(standard.lower_is_better ? "standard.lowerHint" : "standard.higherHint")}>
				<span aria-hidden className="sl-std-dot" />
				<span className="tabular-nums">{t("standard.label", { mark: markLabel(standard.result_type, standard.mark) })}</span>
				<span className="sr-only">{t(standard.lower_is_better ? "standard.lowerHint" : "standard.higherHint")}</span>
			</span>
			{standard.note && <span className="text-gms-ink-3">{standard.note}</span>}
		</span>
	);
}

/** The entry quota beside the shortlist quota: entries that compete, from the event settings. */
export function EntryQuota({ n }: { n: number | null }) {
	const { t } = useTranslation("shortlist");
	const { formatNumber: fmt } = useUiStrings();
	if (n === null) return null;
	return (
		<span className="text-[0.6875rem] whitespace-nowrap text-gms-ink-3 tabular-nums" title={t("quota.entryQuotaHint")}>
			{t("quota.entryQuota", { count: n, n: fmt(n) })}
		</span>
	);
}

/** A PRIMARY row the API reads `meets_standard: false`: an administrator saved it anyway, or the standard moved. */
export function BelowStandardBadge() {
	const { t } = useTranslation("shortlist");
	return (
		<span className="sl-below" title={t("record.belowBadgeHint")}>
			<AlertTriangleIcon className="size-[0.75rem]" />
			{t("record.belowBadge")}
		</span>
	);
}

const PLACEHOLDER: Record<MarkType, string> = { TIME: "1:58.50", DISTANCE: "7.20", WEIGHT: "250", POINTS: "8123", SCORE: "13.533" };
const UNIT_TYPES: readonly MarkType[] = ["DISTANCE", "WEIGHT", "POINTS"];

export interface RecordFieldProps {
	standard: EventQualifyingStandard;
	draft: RecordDraft;
	onChange: (draft: RecordDraft) => void;
	locked: boolean;
	/** The row is RESERVE: a record is optional there. */
	reserve: boolean;
	state?: "pending" | "refused";
	/** "Best record in 100m Freestyle for Porntip Boonma". */
	label: string;
}

/**
 * 0062: the athlete's best record for one event. The mark is typed in the
 * sport's notation and read live with contracts' `parseMark` for the event's
 * result type; the tick / cross is `meetsStandard` against the standard
 * (display only: the API judges a PRIMARY row on save). Date and competition
 * are optional, in a small panel that opens under the field (an absolutely
 * placed popover would be clipped by the event card).
 */
export function RecordField({ standard, draft, onChange, locked, reserve, state, label }: RecordFieldProps) {
	const { t } = useTranslation("shortlist");
	const id = useId();
	const [open, setOpen] = useState(false);
	if (standard.result_type === "NONE") return null;
	const type: MarkType = standard.result_type;
	const verdict = recordVerdict(standard, draft);
	const hasDetails = draft.date !== "" || draft.competition.trim() !== "";
	const summary = [draft.date ? formatDayShort(draft.date) : null, draft.competition.trim() || null].filter(Boolean);
	const today = new Date();
	const max = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
	const verdictText = verdict === "empty" && reserve ? t("record.optionalReserve") : t(`record.verdict.${verdict}`);
	return (
		<div className="sl-rec" data-state={state}>
			<div className="flex flex-wrap items-center gap-x-[0.5rem] gap-y-[0.25rem]">
				<label htmlFor={`${id}-mark`} className="text-[0.71875rem] font-semibold text-gms-ink-2">
					{t("record.label")}
				</label>
				<span className="sl-rec__box" data-verdict={verdict}>
					<input
						id={`${id}-mark`}
						type="text"
						inputMode="decimal"
						autoComplete="off"
						spellCheck={false}
						value={draft.text}
						placeholder={PLACEHOLDER[type]}
						disabled={locked}
						aria-label={label}
						aria-invalid={verdict === "invalid" ? true : undefined}
						aria-describedby={`${id}-verdict ${id}-hint`}
						onChange={(e) => onChange({ ...draft, text: e.target.value })}
					/>
					{UNIT_TYPES.includes(type) && <span className="sl-rec__unit">{t(`record.unit.${type as "DISTANCE" | "WEIGHT" | "POINTS"}`)}</span>}
				</span>
				<span id={`${id}-verdict`} className="sl-rec__verdict" data-verdict={verdict} aria-live="polite">
					{verdict === "meets" ? <CheckIcon className="size-[0.875rem]" /> : verdict === "below" || verdict === "invalid" ? <CloseIcon className="size-[0.875rem]" /> : null}
					{verdictText}
				</span>
				<button
					type="button"
					className="sl-rec__more"
					aria-expanded={open}
					aria-controls={`${id}-details`}
					onClick={() => setOpen((v) => !v)}
					title={t("record.details")}
				>
					<CalendarIcon className="size-[0.875rem] shrink-0" />
					<span className="truncate">{hasDetails ? summary.join(" · ") : t("record.details")}</span>
				</button>
			</div>
			<span id={`${id}-hint`} className="sr-only">
				{t(`record.hint.${type}`)}
			</span>
			{verdict === "invalid" && (
				<p className="mt-[0.25rem] text-[0.6875rem] leading-snug text-gms-bad" aria-hidden>
					{t(`record.hint.${type}`)}
				</p>
			)}
			{open && (
				<div id={`${id}-details`} role="group" aria-label={t("record.detailsAria")} className="sl-rec__panel">
					<label className="grid gap-[0.25rem] text-[0.71875rem] font-semibold text-gms-ink-2">
						{t("record.date")}
						<DateInput
							className="input input-sm w-full font-normal"
							value={draft.date}
							max={max}
							disabled={locked}
							onChange={(v) => onChange({ ...draft, date: v })}
						/>
					</label>
					<label className="grid gap-[0.25rem] text-[0.71875rem] font-semibold text-gms-ink-2">
						{t("record.competition")}
						<input
							type="text"
							className="input input-sm w-full font-normal"
							value={draft.competition}
							maxLength={BEST_MARK_COMPETITION_MAX}
							placeholder={t("record.competitionPlaceholder")}
							disabled={locked}
							onChange={(e) => onChange({ ...draft, competition: e.target.value })}
						/>
					</label>
				</div>
			)}
		</div>
	);
}

/** The Statuses guide: the deadline states and the two roles, from the contract lists and the page's own pills. */
export function useShortlistGuide(withRoles: boolean): { title: string; groups: StatusGuideGroup[] } {
	const { t } = useTranslation("shortlist");
	const groups: StatusGuideGroup[] = [
		{
			key: "deadline",
			heading: t("guide.deadline"),
			items: SHORTLIST_DEADLINE_STATES.map((s) => ({
				key: s,
				label: t(`deadline.state.${s}`),
				description: t(`deadline.hint.${s}`),
				marker: <DeadlinePill state={s} />,
			})),
		},
	];
	if (withRoles)
		groups.push({
			key: "roles",
			heading: t("guide.roles"),
			note: t("guide.rolesNote"),
			items: [
				...SHORTLIST_ROLES.map((r) => ({ key: r, label: t(`role.${r}`), description: t(`role.hint.${r}`), marker: <RoleTag role={r} /> })),
				{ key: "pending", label: t("guide.pending"), description: t("guide.pendingHint"), color: "var(--color-gms-yellow)" },
				{ key: "refused", label: t("guide.refused"), description: t("guide.refusedHint"), color: "var(--color-gms-bad)" },
			],
		});
	return { title: t("guide.title"), groups };
}

/** Her "How the shortlist works" sheet (admin.js:1288): three steps and the quota rule. */
export function HowItWorksSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
	const { t } = useTranslation("shortlist");
	const steps = ["registered", "select", "deadline"] as const;
	return (
		<Modal open={open} onClose={onClose} title={t("help.title")}>
			<ol className="grid gap-[0.375rem] min-[30rem]:grid-cols-3">
				{steps.map((s, i) => (
					<li key={s} className="grid content-start gap-[0.25rem] rounded-field bg-gms-soft p-[0.625rem] text-[0.75rem] leading-snug text-gms-ink-2">
						<span aria-hidden className="grid size-[1.375rem] place-items-center rounded-full bg-gms-navy text-[0.71875rem] font-bold text-white">
							{i + 1}
						</span>
						<b className="text-[0.78125rem] text-gms-navy-ink">{t(`help.steps.${s}.title`)}</b>
						{t(`help.steps.${s}.body`)}
					</li>
				))}
			</ol>
			<p className="mt-[0.75rem] text-[0.8125rem] leading-relaxed text-gms-ink-2">{t("help.quota")}</p>
			<p className="mt-[0.5rem] text-[0.8125rem] leading-relaxed text-gms-ink-2">{t("help.standard")}</p>
			<p className="mt-[0.5rem] text-[0.8125rem] leading-relaxed text-gms-ink-2">{t("help.ovr")}</p>
		</Modal>
	);
}
